import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, DatePicker, Input, Select, Tooltip, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { QuickAddField, QuickAddRow } from './warehousingStyles';
import { LINE_ITEM_BAG, LINE_ITEM_MANPOWER, acceptsHours } from '../warehousing.constants';
import { rateWithCommas } from 'utils/NumberCommaSeprator';

const DATE_FORMAT = 'DD/MM/YYYY';

/**
 * Last date an entry can be filed against in this period: month end, or yesterday if
 * that's earlier — the server rejects today's date outright (today isn't "closed" yet,
 * so its figures aren't final), so the picker must never even offer it.
 */
const lastSelectableDay = (periodMonth) =>
	moment.min(periodMonth.clone().endOf('month'), moment().subtract(1, 'day').endOf('day'));

/** Yesterday when the open month is the current one, otherwise that month's last valid day. */
const defaultEntryDate = (periodMonth) => {
	const yesterday = moment().subtract(1, 'day');
	return yesterday.isSame(periodMonth, 'month') ? yesterday : lastSelectableDay(periodMonth);
};

/**
 * Inline single-row entry beneath each upload panel.
 *
 * `hours` is enabled only for overtime manpower types — the server rejects hours on any
 * other type, so disabling the field keeps that rule visible before submitting.
 *
 * The date is confined to the month selected in the page header: entries belong to the
 * open period, and the backend rejects anything outside it.
 *
 * `disabled` closes the entire row, not just the submit button: a frozen period accepts
 * no new entries at all, so letting values be typed into dead fields only invites the
 * question of why Add Row does nothing.
 */
export default function DailyQuickAdd({
	mode,
	projectName,
	manpowerTypes,
	periodMonth,
	saving,
	disabled,
	disabledReason,
	isCostPlus,
	onSubmit,
}) {
	const isManpower = mode === LINE_ITEM_MANPOWER;
	const isBag = mode === LINE_ITEM_BAG;

	const [entryDate, setEntryDate] = useState(() => defaultEntryDate(periodMonth));
	const [typeId, setTypeId] = useState(undefined);
	const [cost, setCost] = useState('');
	const [costToCustomer, setCostToCustomer] = useState('');
	const [count, setCount] = useState('');
	const [hours, setHours] = useState('');
	const [shipmentCount, setShipmentCount] = useState('');
	const [bagCount, setBagCount] = useState('');

	const monthStart = useMemo(() => periodMonth.clone().startOf('month'), [periodMonth]);
	const monthEnd = useMemo(() => lastSelectableDay(periodMonth), [periodMonth]);

	// Follow the header's month picker rather than stranding a date from the old period
	useEffect(() => {
		setEntryDate((prev) =>
			prev && prev.isSame(periodMonth, 'month') ? prev : defaultEntryDate(periodMonth)
		);
	}, [periodMonth]);

	const hoursAllowed = acceptsHours(manpowerTypes, typeId);
	// Live preview of the entry's total: Cost is per head, so total = Cost x Count.
	const manpowerTotal =
		cost === '' || count === '' ? null : Math.round(Number(cost) * Number(count) * 100) / 100;
	// Cost Plus: the customer's total uses Cost to Customer per head, falling back to the
	// Cost to Company per head when none is entered (the backend treats it the same way).
	const manpowerTotalCtcu =
		manpowerTotal === null
			? null
			: Math.round(
					Number(costToCustomer === '' ? cost : costToCustomer) * Number(count) * 100
			  ) / 100;

	// Switching to a type that doesn't take hours drops whatever was typed, so a stale
	// value can't ride along on the next Add.
	const handleTypeChange = (value) => {
		setTypeId(value);
		if (!acceptsHours(manpowerTypes, value)) setHours('');
	};

	const reset = () => {
		setCost('');
		setCostToCustomer('');
		setCount('');
		setHours('');
		setShipmentCount('');
		setBagCount('');
	};

	const buildManpowerPayload = () => {
		if (!typeId) {
			message.error('Select a manpower type.');
			return null;
		}
		if (cost === '' || count === '') {
			message.error('Cost and count are both required.');
			return null;
		}
		if (isCostPlus && costToCustomer !== '' && Number(costToCustomer) < Number(cost)) {
			message.error('Cost to Customer cannot be less than Cost.');
			return null;
		}
		const payload = { manpower_type_id: typeId, cost: Number(cost), headcount: Number(count) };
		if (isCostPlus && costToCustomer !== '') payload.cost_to_customer = Number(costToCustomer);
		if (hoursAllowed && hours !== '') payload.hours = Number(hours);
		return payload;
	};

	const buildBagPayload = () => {
		if (bagCount === '') {
			message.error('Enter a bag count.');
			return null;
		}
		return { bag_count: Number(bagCount) };
	};

	const buildShipmentPayload = () => {
		if (shipmentCount === '') {
			message.error('Enter a shipment count.');
			return null;
		}
		return { shipment_count: Number(shipmentCount) };
	};

	const handleAdd = async () => {
		if (!entryDate) {
			message.error('Pick a date first.');
			return;
		}

		let modePayload;
		if (isManpower) {
			modePayload = buildManpowerPayload();
		} else if (isBag) {
			modePayload = buildBagPayload();
		} else {
			modePayload = buildShipmentPayload();
		}
		if (!modePayload) return;

		const ok = await onSubmit({ entry_date: entryDate.format('YYYY-MM-DD'), ...modePayload });
		if (ok) reset();
	};

	const typeOptions = (manpowerTypes || []).map((t) => ({ value: t.id, label: t.type_name }));

	const renderManpowerFields = () => (
		<>
			<QuickAddField>
				<span className="field-label">Type</span>
				<Select
					placeholder={typeOptions.length ? 'Select' : 'No types yet'}
					options={typeOptions}
					value={typeId}
					onChange={handleTypeChange}
					disabled={disabled || typeOptions.length === 0}
				/>
			</QuickAddField>
			<QuickAddField>
				<span className="field-label">
					{isCostPlus ? 'Cost to Company per head (₹)' : 'Cost per head (₹)'}
				</span>
				<Input
					type="number"
					min={0}
					value={cost}
					disabled={disabled}
					onChange={(e) => setCost(e.target.value)}
				/>
			</QuickAddField>
			{isCostPlus && (
				<QuickAddField>
					<span className="field-label">Cost to Customer per head (₹)</span>
					<Input
						type="number"
						min={0}
						placeholder="Same as Cost"
						value={costToCustomer}
						disabled={disabled}
						onChange={(e) => setCostToCustomer(e.target.value)}
					/>
				</QuickAddField>
			)}
			<QuickAddField>
				<span className="field-label">Count</span>
				<Input
					type="number"
					min={0}
					value={count}
					disabled={disabled}
					onChange={(e) => setCount(e.target.value)}
				/>
			</QuickAddField>
			<QuickAddField>
				<span className="field-label">
					{isCostPlus ? 'Total Cost to Company (₹)' : 'Total (₹)'}
				</span>
				<Input
					value={manpowerTotal === null ? '' : rateWithCommas(manpowerTotal)}
					placeholder="Cost × Count"
					title="Cost per head × Count"
					disabled
				/>
			</QuickAddField>
			{isCostPlus && (
				<QuickAddField>
					<span className="field-label">Total Cost to Customer (₹)</span>
					<Input
						value={manpowerTotalCtcu === null ? '' : rateWithCommas(manpowerTotalCtcu)}
						placeholder="Cost to Customer × Count"
						title="Cost to Customer per head × Count"
						disabled
					/>
				</QuickAddField>
			)}
			<QuickAddField>
				<span className="field-label">Hours</span>
				<Input
					type="number"
					min={0}
					placeholder={hoursAllowed ? '0' : 'Overtime only'}
					value={hours}
					disabled={disabled || !hoursAllowed}
					title={hoursAllowed ? undefined : 'Hours apply to overtime manpower types only'}
					onChange={(e) => setHours(e.target.value)}
				/>
			</QuickAddField>
		</>
	);

	const renderBagFields = () => (
		<>
			<QuickAddField>
				<span className="field-label">Project</span>
				<Input value={projectName || ''} disabled />
			</QuickAddField>
			<QuickAddField>
				<span className="field-label">Bag Count</span>
				<Input
					type="number"
					min={0}
					value={bagCount}
					disabled={disabled}
					onChange={(e) => setBagCount(e.target.value)}
				/>
			</QuickAddField>
		</>
	);

	const renderShipmentFields = () => (
		<>
			<QuickAddField>
				<span className="field-label">Project</span>
				<Input value={projectName || ''} disabled />
			</QuickAddField>
			<QuickAddField>
				<span className="field-label">Shipment Count</span>
				<Input
					type="number"
					min={0}
					value={shipmentCount}
					disabled={disabled}
					onChange={(e) => setShipmentCount(e.target.value)}
				/>
			</QuickAddField>
		</>
	);

	const renderModeFields = () => {
		if (isManpower) return renderManpowerFields();
		if (isBag) return renderBagFields();
		return renderShipmentFields();
	};

	return (
		<QuickAddRow>
			<QuickAddField>
				<span className="field-label">Date</span>
				<DatePicker
					value={entryDate}
					format={DATE_FORMAT}
					allowClear={false}
					disabled={disabled}
					disabledDate={(current) =>
						current && (current < monthStart || current > monthEnd)
					}
					onChange={setEntryDate}
				/>
			</QuickAddField>

			{renderModeFields()}

			<QuickAddField>
				<Tooltip title={disabled ? disabledReason : undefined}>
					<Button
						icon={<PlusOutlined />}
						loading={saving}
						disabled={disabled}
						onClick={handleAdd}
					>
						Add Row
					</Button>
				</Tooltip>
			</QuickAddField>
		</QuickAddRow>
	);
}

DailyQuickAdd.propTypes = {
	mode: PropTypes.oneOf(['manpower', 'shipment', 'bag']).isRequired,
	projectName: PropTypes.string,
	manpowerTypes: PropTypes.array,
	periodMonth: PropTypes.instanceOf(moment).isRequired,
	saving: PropTypes.bool,
	disabled: PropTypes.bool,
	disabledReason: PropTypes.string,
	isCostPlus: PropTypes.bool,
	onSubmit: PropTypes.func.isRequired,
};

DailyQuickAdd.defaultProps = {
	projectName: '',
	manpowerTypes: [],
	saving: false,
	disabled: false,
	disabledReason: undefined,
	isCostPlus: false,
};
